// 兑奖信息收集页：填写支付宝账号 + 实名 → 提交兑奖
import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Container, Box, Card, CardContent, Typography, Button, Stack,
  TextField, Alert, CircularProgress, Divider, FormControlLabel, Checkbox,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RlPageHeader from '../../components/real-lottery/RlPageHeader';
import { BallDisplay } from '../../components/real-lottery/BallPicker';
import { getRlOrder, submitRlRedeem, getKyc } from '../../services/realLotteryApi';
import { getCurrentRlUser } from '../../components/real-lottery/useRlUser';

export default function RealLotteryRedeemPage() {
  const history = useHistory();
  const { outTradeNo } = useParams();
  const [user, setUser] = useState(null);
  const [order, setOrder] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [alipayAccount, setAlipayAccount] = useState('');
  const [alipayName, setAlipayName] = useState('');
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const u = getCurrentRlUser();
    if (!u) { history.replace('/login-v2'); return; }
    setUser(u);
    Promise.all([
      getRlOrder(outTradeNo, u.userId),
      getKyc(u.userId),
    ]).then(([o, k]) => {
      if (o.success) setOrder(o.data);
      if (k.success && k.data && k.data.maskedName) {
        setKyc(k.data);
        // 默认填实名（用户可改为支付宝实名）
      }
      setLoading(false);
    });
  }, [outTradeNo]);

  const handleSubmit = async () => {
    setError('');
    if (!alipayAccount.trim()) return setError('请输入支付宝账号');
    if (!alipayName.trim()) return setError('请输入支付宝实名');
    if (!agree) return setError('请阅读并同意《兑奖服务协议》');
    setSubmitting(true);
    try {
      const res = await submitRlRedeem(outTradeNo, {
        userId: user.userId,
        alipayAccount: alipayAccount.trim(),
        alipayName: alipayName.trim(),
      });
      if (res.success) {
        history.replace(`/real-lottery/order/${outTradeNo}`);
      } else {
        setError(res.message || '提交失败');
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (!order) {
    return (
      <Box>
        <RlPageHeader title="兑奖" />
        <Container maxWidth="sm" sx={{ pt: 4 }}>
          <Alert severity="error">订单不存在或无权访问</Alert>
        </Container>
      </Box>
    );
  }
  if (!['won', 'redeem_failed'].includes(order.status)) {
    return (
      <Box>
        <RlPageHeader title="兑奖" />
        <Container maxWidth="sm" sx={{ pt: 4 }}>
          <Alert severity="info">当前订单状态为「{order.statusText}」，不可兑奖</Alert>
          <Button fullWidth sx={{ mt: 2 }} variant="outlined"
            onClick={() => history.replace(`/real-lottery/order/${outTradeNo}`)}>
            返回订单
          </Button>
        </Container>
      </Box>
    );
  }
  if (order.isHugeWin) {
    return (
      <Box>
        <RlPageHeader title="兑奖" />
        <Container maxWidth="sm" sx={{ pt: 4 }}>
          <Alert severity="warning">
            单注奖金 ≥ 10 万元，根据国家规定需到福彩中心线下兑奖。请联系客服获取协助。
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f7', minHeight: '100vh', pb: 12 }}>
      <RlPageHeader title="提交兑奖信息" />
      <Container maxWidth="sm" sx={{ pt: 2 }}>
        {/* 中奖摘要 */}
        <Card sx={{ mb: 2, borderRadius: 2,
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', color: '#fff' }}>
          <CardContent>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              恭喜中奖 · 第 {order.period} 期
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, my: 1 }}>
              ¥{order.afterTaxAmountYuan}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {order.taxAmountFen > 0
                ? `税前 ¥${order.winAmountYuan}，已扣 20% 偶然所得税`
                : '小额奖金，无需扣税'}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <BallDisplay red={order.redBalls} blue={order.blueBall} size={26} />
            </Box>
          </CardContent>
        </Card>

        {/* 兑奖渠道说明 */}
        <Alert severity="info" sx={{ mb: 2 }}>
          目前仅支持「支付宝」收款。请填写收款方支付宝账号和实名（必须与本人实名一致）。
        </Alert>

        {/* 表单 */}
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <AccountBalanceWalletIcon color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>支付宝收款信息</Typography>
            </Stack>
            <TextField
              fullWidth
              label="支付宝账号（手机号或邮箱）"
              value={alipayAccount}
              onChange={(e) => setAlipayAccount(e.target.value)}
              margin="normal"
              autoFocus
            />
            <TextField
              fullWidth
              label="支付宝实名"
              value={alipayName}
              onChange={(e) => setAlipayName(e.target.value)}
              margin="normal"
              helperText={kyc ? `实名认证：${kyc.maskedName}（需与支付宝实名一致）` : ''}
            />
            <FormControlLabel
              control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
              label={
                <Typography variant="caption">
                  我已阅读并同意《兑奖服务协议》，确认账号信息真实有效
                </Typography>
              }
              sx={{ mt: 1 }}
            />
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ px: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            • 请仔细核对账号，提交后无法修改
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            • 平台一般 1-3 个工作日内打款到账
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            • 如打款失败可重新提交（订单状态会变为「兑奖失败」）
          </Typography>
        </Box>
      </Container>

      <Box sx={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        backgroundColor: '#fff', borderTop: '1px solid rgba(0,0,0,0.08)',
        py: 1.5, px: 2, zIndex: 10,
      }}>
        <Container maxWidth="sm" disableGutters>
          <Button
            fullWidth variant="contained" color="success" size="large"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ py: 1.3 }}
          >
            {submitting ? '提交中…' : `确认兑奖（¥${order.afterTaxAmountYuan}）`}
          </Button>
        </Container>
      </Box>
    </Box>
  );
}
